import { FormSchemaAccessModel } from './schema_access_model';

class FormSchemaResolverModel {

    constructor(rulesModel, ...formSchemas) {
        this.rulesModel = rulesModel;
        this._formSchemas = formSchemas;
        this.accessModel = new FormSchemaAccessModel(...formSchemas);
    }

    /**
     * Get an array of all resolved element schemas - FieldSets and FieldRows. If an element is not the result of a
     * resolution, it is ignored.
     * @returns {Object} - The set of resolved element schemas and fieldSets.
     */
    allResolvedElements = () => {
        const resolvedElements = {};
        const resolvedFieldSets = {};

        // Get top-level page elements. These are the starting point for traversing through all elements
        const currentElements = this._formSchemas.map((schema) => [schema.formProperties.stateKey, schema.pages])
            .reduce((elements, [stateKey, pages]) => {
                if (!elements[stateKey]) {
                    // eslint-disable-next-line no-param-reassign
                    elements[stateKey] = [];
                }
                for (const page of pages) {
                    if (!page.elements) {
                        continue;
                    }
                    elements[stateKey].push(...page.elements);
                }
                return elements;
            }, {});

        for (const stateKey of Object.keys(currentElements)) {
            resolvedElements[stateKey] = [];
            resolvedFieldSets[stateKey] = [];

            const currentFormElements = currentElements[stateKey];
            while (currentFormElements.length > 0) {
                const el = currentFormElements.pop();
                if (el.elType === 'fieldSet' && fieldSetHasReferences(el)) {
                    // Referenced FieldSet
                    const resolvedFieldSet = this.resolveFieldSetReference(el);
                    resolvedElements[stateKey].push(...resolvedFieldSet.elements);
                    resolvedFieldSets[stateKey].push(resolvedFieldSet);
                }
            }
        }
        return { resolvedElements, resolvedFieldSets };
    };

    /**
     * Use the rulesModel to remap elementRules to new identifiers and stateKeys.
     * @param {Array} elementRules - The set of element rules to resolve
     * @param {String} identifierRemap - The relative identifier to use when remapping stateKeys and identifiers. This is
     * relative to the form state and is the new 'container' id for the fieldSet or fieldRow being resolved, eg
     * 'helpers.sandbox.changeEnergyType'
     * @returns {Array} - The resolved elementRules
     */
    resolveRules = (elementRules, identifierRemap) => {
        if (!elementRules) {
            return [];
        }
        return elementRules.map((rule) => this.rulesModel.remapElementRule(rule, identifierRemap));
    };

    /**
     * Create a new FieldSet schema, based on an existing FieldSet schema, with resolved rules and FieldRows.
     * @param {Object} fieldSet - The FieldSet schema with references that need to be resolved.
     * @returns {Object} resolvedFieldSet - The FieldSet schema with all references resolved
     */
    resolveFieldSetReference = (fieldSet) => {
        if (fieldSet.reference) {
            // get the FieldSet that is referenced
            const baseFieldSet = this.accessModel.getFieldSet(fieldSet.reference);
            // extract all base props except reference and identifierRemap so that the base props can be used for the new FieldSet
            const { reference, identifierRemap, ...baseProps } = fieldSet;
            // Clone the base FieldSet - using JSON parse / stringify is the easiest and quickest way to make a deep clone of a JS object
            const resolvedFieldSet = Object.assign(JSON.parse(JSON.stringify(baseFieldSet)), baseProps);

            // Resolve identifier
            resolvedFieldSet.identifier = `${identifierRemap}.${baseFieldSet.identifier}`;
            // Resolve elements
            resolvedFieldSet.elements = baseFieldSet.elements.map(
                (fieldRow) => this.resolveFieldRowReference(fieldRow, identifierRemap)
            );
            // Resolve rules
            resolvedFieldSet.rules = this.resolveRules(
                baseFieldSet.rules,
                identifierRemap
            );
            return resolvedFieldSet;
        }

        // The FieldSet may contain reference FieldRows that need to be resolved.
        // Clone the base FieldSet - using JSON parse / stringify is the easiest and quickest way to make a deep clone of a JS object
        const resolvedFieldSet = JSON.parse(JSON.stringify(fieldSet));
        resolvedFieldSet.elements = fieldSet.elements.map(
            (fieldRow) => {
                if (!fieldRow.reference) {
                    // no reference on the Fieldrow, we just return it as is
                    return fieldRow;
                }
                // Get the base FieldRow that is referenced
                const baseFieldRow = this.accessModel.getFieldRow(fieldRow.reference);
                // Resolve the referenced FieldRow
                return this.resolveFieldRowReference(baseFieldRow, fieldRow.identifierRemap);
            });
        return resolvedFieldSet;
    };

    /**
     * Create a new FieldRow schema, based on an existing FieldRow schema, with remapped rules and identifier
     * @param {Object} baseFieldRow - The referenced FieldRow schema
     * @param {String} identifierRemap - A base identifier to remap the existing FieldRow identifier to a different state value
     * @returns {Object} resolvedFieldRow -
     */
    resolveFieldRowReference = (baseFieldRow, identifierRemap) => {
        // Clone the base FieldRow - using JSON parse / stringify is the easiest and quickest way to make a deep clone of a JS object
        const resolvedFieldRow = JSON.parse(JSON.stringify(baseFieldRow));
        // Remap identifier
        if (resolvedFieldRow.backendIdentifier) {
            resolvedFieldRow.backendIdentifier = `${identifierRemap}.${baseFieldRow.backendIdentifier}`;
        }
        resolvedFieldRow.identifier = `${identifierRemap}.${baseFieldRow.identifier}`;
        // Remap rules
        resolvedFieldRow.rules = this.resolveRules(resolvedFieldRow.rules, identifierRemap);
        return resolvedFieldRow;
    };

    /**
     * Resolve all fieldSet and FieldRow references in the given page schema.
     * @param {Object} page - A page schema
     * @param {Array} keys - The keys that should be resolved from the page object
     * @returns {Object} resolvedPage - A new page schema with all references resolved
     */
    resolvePageReferences = (page, keys = ['name', 'fadeIn']) => {
        let resolvedPage = { elements: [] };
        for (const key of keys) {
            resolvedPage = { ...resolvedPage, [key]: page[key] };
        }
        for (const element of page.elements) {
            resolvedPage.elements.push(this.resolveFieldSetReference(element));
        }
        return resolvedPage;
    };

}

/**
 * Returns true if the supplied FieldSet or any of its elements have references
 *
 * Exported from here so FormSchemaResolverModel can also make use of it.
 *
 * @param {Object} fieldSet - The FieldSet schema
 * @returns {Boolean} - True if fieldSet or any of it's elements have references
 */
export const fieldSetHasReferences = (fieldSet) => {
    if (fieldSet.reference) {
        return true;
    }
    return fieldSet.elements.filter((el) => el.reference !== undefined).length > 0;
};

export { FormSchemaResolverModel };
